/**
 * Framework-free document-head synchronizer for client-side navigation.
 *
 * The prerendered static HTML ships correct per-route metadata, but a
 * client-side (react-router) navigation does not reload the document —
 * without this module the child page would keep the previous page's
 * title, description, canonical, Open Graph tags, and JSON-LD.
 *
 * `headStateForPath` is pure (unit-testable without a DOM);
 * `syncDocumentHead` applies it to a document-like object.
 */

import { ROUTE_META, canonicalFor, type RouteMeta } from './content/meta'

export interface HeadState {
  title: string
  description: string
  canonical: string
  robots: string
  ogTitle: string
  ogDescription: string
  ogUrl: string
  ogType: string
  jsonLd: string | null
}

const FALLBACK: RouteMeta = ROUTE_META[0] // homepage metadata as default

export function headStateForPath(path: string): HeadState {
  const meta = ROUTE_META.find((r) => r.path === path) ?? FALLBACK
  return {
    title: meta.title,
    description: meta.description,
    canonical: canonicalFor(meta.path),
    robots: 'index,follow',
    ogTitle: meta.title,
    ogDescription: meta.description,
    ogUrl: canonicalFor(meta.path),
    ogType: meta.ogType ?? 'website',
    jsonLd: meta.jsonLd ?? null,
  }
}

/** Minimal document surface this module needs (a real Document satisfies it). */
export interface ElementLike {
  getAttribute(name: string): string | null
  setAttribute(name: string, value: string): void
  textContent: string
  remove(): void
}

export interface HeadDoc {
  title: string
  head: { appendChild(node: unknown): unknown }
  createElement(tag: string): ElementLike
  querySelector(selectors: string): ElementLike | null
}

function upsertMeta(
  doc: HeadDoc,
  selector: string,
  content: string,
): void {
  let el = doc.querySelector(selector)
  if (!el) {
    el = doc.createElement('meta')
    const [attrName, attrValue] = selector.match(/\[(name|property)="([^"]+)"\]/)!.slice(1)
    el.setAttribute(attrName, attrValue)
    doc.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical(doc: HeadDoc, href: string): void {
  let el = doc.querySelector('link[rel="canonical"]')
  if (!el) {
    el = doc.createElement('link')
    el.setAttribute('rel', 'canonical')
    doc.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

const JSONLD_MARKER = 'data-seo-jsonld'

function syncJsonLd(doc: HeadDoc, jsonLd: string | null): void {
  // Always remove the previous route's JSON-LD first — a child page must
  // never inherit the homepage's structured data.
  const existing = doc.querySelector(`script[${JSONLD_MARKER}]`)
  if (existing) {
    existing.remove()
  }
  if (jsonLd) {
    const script = doc.createElement('script')
    script.setAttribute('type', 'application/ld+json')
    script.setAttribute(JSONLD_MARKER, '')
    script.textContent = jsonLd
    doc.head.appendChild(script)
  }
}

/** Apply the metadata for `path` to the document head. */
export function syncDocumentHead(path: string, doc: HeadDoc): HeadState {
  const state = headStateForPath(path)
  doc.title = state.title
  upsertMeta(doc, 'meta[name="description"]', state.description)
  upsertMeta(doc, 'meta[name="robots"]', state.robots)
  upsertCanonical(doc, state.canonical)
  upsertMeta(doc, 'meta[property="og:title"]', state.ogTitle)
  upsertMeta(doc, 'meta[property="og:description"]', state.ogDescription)
  upsertMeta(doc, 'meta[property="og:url"]', state.ogUrl)
  upsertMeta(doc, 'meta[property="og:type"]', state.ogType)
  syncJsonLd(doc, state.jsonLd)
  return state
}
